import React, { useState, memo, useMemo, useEffect, useRef } from 'react';
import { Check, Play, SquareTerminal } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '@/hooks/use-toast';
import { putRunPayload } from '@/services/runStore';
import hljs from 'highlight.js/lib/core';
import {
  Clipboard as Copy,
} from "../../packages/svgs";
// Import highlight.js languages
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import java from 'highlight.js/lib/languages/java';
import cpp from 'highlight.js/lib/languages/cpp';
import csharp from 'highlight.js/lib/languages/csharp';
import php from 'highlight.js/lib/languages/php';
import ruby from 'highlight.js/lib/languages/ruby';
import go from 'highlight.js/lib/languages/go';
import rust from 'highlight.js/lib/languages/rust';
import swift from 'highlight.js/lib/languages/swift';
import kotlin from 'highlight.js/lib/languages/kotlin';
import sql from 'highlight.js/lib/languages/sql';
import bash from 'highlight.js/lib/languages/bash';
import json from 'highlight.js/lib/languages/json';
import xml from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import yaml from 'highlight.js/lib/languages/yaml';
import markdown from 'highlight.js/lib/languages/markdown';
import dockerfile from 'highlight.js/lib/languages/dockerfile';
import powershell from 'highlight.js/lib/languages/powershell';

// Import only atom-one-dark - we'll override colors for light mode with inline styles
import 'highlight.js/styles/atom-one-dark.css';

// Theme color definitions
const THEME_COLORS = {
  dark: {
    background: '#292929',
    headerBg: '#202020',
    text: '#abb2bf',
    keyword: '#c678dd',
    string: '#98c379',
    comment: '#5c6370',
    number: '#d19a66',
    tag: '#e06c75',
    attribute: '#d19a66',
  },
  light: {
    background: '#fafafa',
    headerBg: '#f0f0f0',
    text: '#383a42',
    keyword: '#a626a4',
    string: '#50a14f',
    comment: '#a0a1a7',
    number: '#986801',
    tag: '#e45649',
    attribute: '#986801',
  },
};

// Register languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('java', java);
hljs.registerLanguage('cpp', cpp);
hljs.registerLanguage('csharp', csharp);
hljs.registerLanguage('php', php);
hljs.registerLanguage('ruby', ruby);
hljs.registerLanguage('go', go);
hljs.registerLanguage('rust', rust);
hljs.registerLanguage('swift', swift);
hljs.registerLanguage('kotlin', kotlin);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('json', json);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('css', css);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('dockerfile', dockerfile);
hljs.registerLanguage('powershell', powershell);

// Language aliases
const languageAliases: Record<string, string> = {
  'js': 'javascript',
  'ts': 'typescript',
  'py': 'python',
  'sh': 'bash',
  'shell': 'bash',
  'yml': 'yaml',
  'md': 'markdown',
  'c++': 'cpp',
  'c#': 'csharp',
  'cs': 'csharp',
  'rs': 'rust',
  'kt': 'kotlin',
  'ps': 'powershell',
  'ps1': 'powershell',
  'chart': 'json',
  'chart-json': 'json',
  'chart-spec': 'json',
  'visualization': 'json',
  'viz': 'json',
};

interface CodeBlockProps {
  code: string;
  language?: string;
  showLineNumbers?: boolean;
  isDarkMode?: boolean;
  isStreaming?: boolean;
}

const CodeBlockComponent = ({ code, language = 'javascript', showLineNumbers = false, isDarkMode = false, isStreaming = false }: CodeBlockProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLElement>(null);

  // Normalize language
  const normalizedLanguage = useMemo(() => {
    const lang = (language || '').toLowerCase().trim();
    return languageAliases[lang] || lang || 'plaintext';
  }, [language]);

  // Auto-detect language if not specified or is plaintext
  const detectedLanguage = useMemo(() => {
    if (normalizedLanguage && normalizedLanguage !== 'plaintext' && normalizedLanguage !== 'text') {
      return normalizedLanguage;
    }

    const sample = (code || '').slice(0, 600);
    if (/^\s*\{[\s\S]*\}\s*$/.test(code) || /"\w+"\s*:/.test(sample)) return 'json';
    if (/^#!\/.+\b(sh|bash|zsh)\b/.test(sample) || /(\bfor\b|\bif\b|\becho\b).*;?\s*$/.test(sample)) return 'bash';
    if (/<[a-zA-Z!][\s\S]*?>/.test(sample)) return 'html';
    if (/\b(def|import|from)\b/.test(sample)) return 'python';
    if (/\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b/i.test(sample)) return 'sql';
    if (/\bpackage\b|\bpublic\b\s+\bclass\b/.test(sample)) return 'java';
    if (/\busing\s+System;/.test(sample)) return 'csharp';
    if (/(interface|type)\s+\w+/.test(sample) || /:\s*\w+/.test(sample)) return 'typescript';
    if (/(import|export)\s+.*from\s+['"].+['"];?/.test(sample)) return 'javascript';
    return 'plaintext';
  }, [code, normalizedLanguage]);

  const canHighlight = useMemo(() => {
    if (!detectedLanguage || detectedLanguage === 'plaintext' || detectedLanguage === 'text') return false;
    return Boolean(hljs.getLanguage(detectedLanguage));
  }, [detectedLanguage]);

  // Apply syntax highlighting only when not streaming
  useEffect(() => {
    if (!isStreaming && codeRef.current && canHighlight) {
      try {
        hljs.highlightElement(codeRef.current);
      } catch (error) {
        console.error('Highlight.js error:', error);
      }
    }
  }, [code, canHighlight, isStreaming]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Copy failed",
        description: "Could not copy code to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleRunCode = () => {
    if (!['html', 'css', 'javascript', 'js', 'ts', 'typescript'].includes(detectedLanguage)) return;

    try {
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      const bytes = new Uint8Array(12);
      window.crypto.getRandomValues(bytes);
      const runId = Array.from(bytes)
        .map((b) => alphabet[b % alphabet.length])
        .join('');

      const payload = {
        code,
        language: detectedLanguage,
        originUrl: window.location.href,
        createdAt: Date.now(),
      };

      void (async () => {
        try {
          await putRunPayload(runId, payload);

          const url = `/run/${runId}`;
          const isStandalone =
            window.matchMedia?.('(display-mode: standalone)')?.matches ||
            // iOS Safari
            (window.navigator as any)?.standalone === true;

          if (isStandalone) {
            window.location.href = url;
            return;
          }

          const a = document.createElement('a');
          a.href = url;
          a.target = '_blank';
          // Intentionally do NOT set rel=noopener here so the runner can focus the opener tab on Back.
          document.body.appendChild(a);
          a.click();
          a.remove();
        } catch (e) {
          console.error('Failed to persist run payload:', e);
          toast({
            title: 'Run failed',
            description: 'Could not prepare the runner.',
            variant: 'destructive',
          });
        }
      })();
    } catch (error) {
      console.error('Failed to run code:', error);
      toast({
        title: 'Run failed',
        description: 'Could not open the runner page.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div
      className={`relative rounded-lg overflow-hidden my-4 border ${isDarkMode ? 'border-border/50' : 'border-gray-200'}`}
      style={{ backgroundColor: isDarkMode ? THEME_COLORS.dark.background : THEME_COLORS.light.background }}
    >
      {/* Header with language badge and copy button */}
      <div
        className={`flex items-center justify-between px-2 py-1 ${isDarkMode ? 'bg-[#202020]' : 'bg-[#f0f0f0]'
          }`}
      >
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-medium lowercase select-none tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
          >
            {detectedLanguage}
          </span>
        </div>

        <div className="flex gap-1">
          {detectedLanguage === 'html' && (
            <div className="relative group">
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 w-7 p-0 ${isDarkMode
                  ? 'text-white hover:bg-white/10'
                  : 'text-black/80 hover:bg-black/5'
                  }`}
                onClick={handleRunCode}
              >
                <SquareTerminal className="h-3.5 w-3.5" />
              </Button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Run
              </div>
            </div>
          )}
          <div className="relative group">
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 w-7 p-0 text-xs font-medium select-none ${isDarkMode
                ? 'text-white hover:bg-white/10'
                : 'text-black/80 hover:bg-black/5'
                }`}
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              Copy
            </div>
          </div>
        </div>
      </div>

      {/* Code content */}
      <div
        className="overflow-x-auto"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: isDarkMode ? '#4a4a4a #2a2a2a' : '#c1c1c1 #f1f1f1',
          backgroundColor: isDarkMode ? THEME_COLORS.dark.background : THEME_COLORS.light.background,
        }}
      >
        <pre
          className="!m-0 !p-4"
          style={{
            backgroundColor: isDarkMode ? THEME_COLORS.dark.background : THEME_COLORS.light.background,
          }}
        >
          <code
            ref={codeRef}
            className={`${canHighlight ? `language-${detectedLanguage}` : ''} text-sm leading-relaxed ${(!isStreaming && canHighlight) ? 'hljs' : ''}`}
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
              tabSize: 2,
              color: isDarkMode ? THEME_COLORS.dark.text : THEME_COLORS.light.text,
              backgroundColor: 'transparent',
              scrollbarWidth: 'thin',
              scrollbarColor: isDarkMode ? '#4a4a4a #2a2a2a' : '#c1c1c1 #f1f1f1',
            }}
          >
            {code}
          </code>
        </pre>
      </div>
    </div>
  );
};

export default memo(CodeBlockComponent);